import Link from "next/link";
import { useLineQuoteUrl } from "lib/line";

function LineIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 flex-none" fill="none" aria-hidden="true">
      <path d="M20 11.2c0 4.1-3.6 7.4-8 7.4-.7 0-1.4-.1-2.1-.3L6 20l1.1-3.1C5.2 15.5 4 13.5 4 11.2 4 7.1 7.6 3.8 12 3.8s8 3.3 8 7.4Z" fill="currentColor" />
      <circle cx="8.5" cy="11.2" r="1" fill="#06C755" />
      <circle cx="12" cy="11.2" r="1" fill="#06C755" />
      <circle cx="15.5" cy="11.2" r="1" fill="#06C755" />
    </svg>
  );
}

export default function BlogServiceCta({ placement = "inline" }: { placement?: "inline" | "footer" }) {
  const lineUrl = useLineQuoteUrl();

  if (placement === "footer") {
    return (
      <aside className="mt-14 overflow-hidden rounded-2xl border border-[#e3d16d] bg-[#fff8cf]" aria-label="บริการจาก KMCP Accounting">
        <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-8 lg:p-10">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-semibold tracking-[0.12em] text-[#806500]">ปรึกษาทีมบัญชี</p>
            <h2 className="mt-3 max-w-xl text-xl font-semibold leading-snug text-gray-950 sm:text-2xl">
              ต้องการคำแนะนำที่ตรงกับข้อมูลของธุรกิจคุณ?
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-7 text-slate-700 sm:text-base">
              ส่งข้อมูลกิจการและงานที่ต้องการให้ทีม KMCP ตรวจเบื้องต้น เราจะแจ้งเอกสาร ขอบเขต และค่าใช้จ่ายก่อนเริ่มงาน
            </p>
            <div className="mt-6 grid max-w-xl gap-3 sm:grid-cols-2">
              <a href={lineUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-12 w-full items-center justify-center gap-2.5 rounded-full bg-[#06C755] px-5 py-3 text-center text-sm font-semibold leading-tight text-white shadow-[0_10px_24px_-14px_rgba(6,199,85,0.9)] transition hover:bg-[#05b94e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06C755] focus-visible:ring-offset-2">
                <LineIcon />
                <span>สอบถามทาง LINE OA</span>
              </a>
              <Link href="/pricing" className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-white px-5 py-3 text-center text-sm font-semibold leading-tight text-gray-950 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.14)] transition hover:bg-[#fffdf2]">
                <span>ดูแพ็กเกจบริการ</span>
              </Link>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <Link href="/services/accounting" className="group flex min-h-28 flex-col justify-between rounded-2xl bg-white/85 p-5 shadow-[0_12px_30px_-24px_rgba(15,23,42,0.45)] transition hover:-translate-y-0.5 hover:bg-white sm:p-6">
              <span className="text-sm font-semibold text-[#806500]">บัญชีรายเดือน</span>
              <span className="mt-4 text-base font-semibold text-gray-950">ดูขอบเขตบริการ</span>
            </Link>
            <Link href="/services/registration" className="group flex min-h-28 flex-col justify-between rounded-2xl bg-white/85 p-5 shadow-[0_12px_30px_-24px_rgba(15,23,42,0.45)] transition hover:-translate-y-0.5 hover:bg-white sm:p-6">
              <span className="text-sm font-semibold text-[#806500]">จดทะเบียนธุรกิจ</span>
              <span className="mt-4 text-base font-semibold text-gray-950">ดูขั้นตอนและราคา</span>
            </Link>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="my-10 rounded-3xl border border-[#e2cf6c] bg-[#fff9d9] p-6 sm:p-8" aria-label="ปรึกษางานบัญชีและจดทะเบียน">
      <div className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <p className="text-xs font-semibold tracking-[0.16em] text-[#806500]">อ่านแล้วอยากตรวจให้ตรงกับกิจการ?</p>
          <p className="mt-3 text-xl font-semibold leading-snug text-gray-950">
            ให้ทีม KMCP ช่วยประเมินขอบเขตงานจากข้อมูลจริง
          </p>
          <p className="mt-3 text-sm leading-7 text-slate-700">
            แจ้งประเภทธุรกิจ จำนวนเอกสาร หรือเรื่องจดทะเบียนที่ต้องการ ทีมงานจะแจ้งรายการข้อมูลและค่าใช้จ่ายก่อนเริ่มงาน
          </p>
        </div>
        <div className="flex sm:min-w-56 sm:justify-end">
          <a href={lineUrl} target="_blank" rel="noreferrer" className="inline-flex h-12 w-full items-center justify-center gap-2.5 whitespace-nowrap rounded-full bg-[#06C755] px-5 text-sm font-semibold leading-none text-white shadow-[0_10px_24px_-14px_rgba(6,199,85,0.9)] transition hover:bg-[#05b94e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06C755] focus-visible:ring-offset-2 sm:w-auto">
            <LineIcon />
            สอบถามทาง LINE OA
          </a>
        </div>
      </div>
    </aside>
  );
}
